import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import TagPill from '@/components/ui/TagPill'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { useSEO } from '@/hooks/useSEO'
import type { Artifact } from '@/types'
import * as api from '@/lib/api'

export default function ArtifactDetailPage() {
  const { slug } = useParams<{ slug: string }>()
  const [loading, setLoading] = useState(true)
  const [artifact, setArtifact] = useState<Artifact | null>(null)

  // SEO
  useSEO({
    title: artifact?.title,
    description: artifact?.description?.slice(0, 160),
    image: artifact?.image_url,
    url: artifact ? `https://thearchivenetwork.com/artifact/${artifact.slug}` : undefined,
  })

  useEffect(() => {
    async function loadData() {
      if (!slug) return
      try {
        const data = await api.getArtifact(slug)
        setArtifact(data)
      } catch (error) {
        console.error('Failed to load artifact:', error)
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [slug])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!artifact) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-display text-2xl text-archive-cream">Artifact Not Found</h1>
          <Link to="/artifacts" className="mt-4 btn-secondary inline-block">
            Browse Artifacts
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="animate-fade-in py-16 sm:py-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="mb-8">
          <ol className="flex items-center gap-2 text-sm text-archive-muted">
            <li><Link to="/artifacts" className="hover:text-archive-gold">Artifacts</Link></li>
            <li>/</li>
            <li className="text-archive-text">{artifact.title}</li>
          </ol>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Image */}
          <div className="aspect-square bg-archive-slate rounded-lg overflow-hidden">
            <img
              src={artifact.image_url}
              alt={artifact.title}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Details */}
          <div className="flex flex-col">
            {/* Archive badge */}
            <TagPill
              label={artifact.archive_key}
              variant="archive"
              size="md"
            />

            {/* Title */}
            <h1 className="mt-4 font-display text-3xl sm:text-4xl text-archive-cream">
              {artifact.title}
            </h1>

            {/* Subtitle */}
            {artifact.subtitle && (
              <p className="mt-2 text-lg text-archive-muted italic">
                {artifact.subtitle}
              </p>
            )}

            {/* Catalog Label */}
            <div className="mt-8 p-6 bg-archive-charcoal rounded-lg border border-archive-stone/50">
              <h2 className="font-display text-sm text-archive-gold uppercase tracking-wider mb-3">
                Catalog Description
              </h2>
              <p className="text-archive-text leading-relaxed">
                {artifact.description}
              </p>
            </div>

            {/* Metadata */}
            <div className="mt-6 grid grid-cols-2 gap-4">
              <div>
                <span className="text-xs text-archive-muted uppercase tracking-wider">Type</span>
                <p className="mt-1 text-archive-text capitalize">{artifact.type}</p>
              </div>
              <div>
                <span className="text-xs text-archive-muted uppercase tracking-wider">Archive</span>
                <p className="mt-1 text-archive-text capitalize">{artifact.archive_key}</p>
              </div>
            </div>

            {/* Tags */}
            {artifact.tags.length > 0 && (
              <div className="mt-6">
                <span className="text-xs text-archive-muted uppercase tracking-wider">Tags</span>
                <div className="mt-2 flex flex-wrap gap-2">
                  {artifact.tags.map((tag) => (
                    <TagPill key={tag} label={tag} size="md" />
                  ))}
                </div>
              </div>
            )}

            {/* Related Themes */}
            {artifact.related_theme_slugs.length > 0 && (
              <div className="mt-6">
                <span className="text-xs text-archive-muted uppercase tracking-wider">Related Themes</span>
                <div className="mt-2 flex flex-wrap gap-2">
                  {artifact.related_theme_slugs.map((slug) => (
                    <TagPill
                      key={slug}
                      label={slug.replace(/-/g, ' ')}
                      linkTo={`/themes/${slug}`}
                      variant="theme"
                      size="md"
                    />
                  ))}
                </div>
              </div>
            )}

            {/* CTA */}
            <div className="mt-auto pt-8">
              <a
                href={artifact.printify_product_url}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full btn-primary justify-center text-lg"
              >
                Collect This Artifact
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
              <p className="mt-3 text-center text-sm text-archive-muted">
                Opens Printify store for secure checkout
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
